import { Component, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDialog } from '@angular/material/dialog';

/** How long the user gets to answer the prompt. */
const COUNTDOWN_SECONDS = 10;

/**
 * Absolute instant the countdown ends, shared by every tab. The tabs raise the
 * prompt at slightly different moments, so the first one to open it fixes the
 * deadline and the rest read it instead of starting their own count.
 */
const DEADLINE_KEY = 'logoutPromptDeadline';

@Component({
    selector: 'app-log-out-modal',
    imports: [MatProgressBarModule],
    templateUrl: './log-out-modal.component.html',
    styleUrl: './log-out-modal.component.scss'
})
export class LogOutModalComponent implements OnDestroy {
  constructor(public activeModal: NgbActiveModal,
      private dialog: MatDialog) {}

  secondsToLogout = COUNTDOWN_SECONDS;
  private timerId: any;
  private deadline = 0;

  /** Countdown as a 0-100 value for the progress bar. */
  get progress(): number {
    return (this.secondsToLogout / COUNTDOWN_SECONDS) * 100;
  }

  ngOnInit() {
    this.startTimer();
  }

  ngOnDestroy() {
    if (this.timerId) clearInterval(this.timerId);

    // Left behind, the stale deadline would be in the past when the prompt is
    // raised again, expiring the next countdown the moment it opens. The tabs
    // close within moments of each other, so this clears only the deadline this
    // instance ran on: a later prompt's is not this one's to remove.
    if (Number(localStorage.getItem(DEADLINE_KEY)) === this.deadline) {
      localStorage.removeItem(DEADLINE_KEY);
    }
  }

  startTimer() {
    this.deadline = this.resolveDeadline();

    this.tick(this.deadline);
    this.timerId = setInterval(() => this.tick(this.deadline), 250);
  }

  /** The deadline another tab already set, or a new one if this tab is first. */
  private resolveDeadline(): number {
    const stored = Number(localStorage.getItem(DEADLINE_KEY));

    // Anything already past belongs to a previous prompt that did not clean up.
    if (stored && stored > Date.now()) {
      return stored;
    }

    const deadline = Date.now() + COUNTDOWN_SECONDS * 1000;
    localStorage.setItem(DEADLINE_KEY, deadline.toString());
    return deadline;
  }

  /**
   * Polled faster than once a second so the tabs that joined late land on the same
   * displayed number as the tab that opened first, instead of being offset by
   * however long after it they happened to start.
   */
  private tick(deadline: number) {
    const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
    this.secondsToLogout = remaining;

    if (remaining === 0) {
      clearInterval(this.timerId);
      this.executeLogout();
    }
  }

  executeLogout() {
    this.activeModal.close(true);
    this.dialog.closeAll();
  }
}
